import { useState, useRef, useEffect } from 'react';
import { Send, Paperclip, Dot, AlertCircle } from 'lucide-react';
import { useChatStore } from '../../../stores/chatStore';
import { useGameStore } from '../../../stores/gameStore';
import { playSound } from '../../../utils/soundService';
import { Avatar } from '../../ui/Avatar';

export function ChatApp() {
  const conversations = useChatStore((state) => state.conversations);
  const selectedConversationId = useChatStore(
    (state) => state.selectedConversationId,
  );
  const selectConversation = useChatStore((state) => state.selectConversation);
  const addMessage = useChatStore((state) => state.addMessage);
  const ndaSigned = useGameStore((state) => state.ndaSigned);
  const getGameTime = useGameStore((state) => state.getGameTime);

  const selectedConversation = conversations.find(
    (conv) => conv.id === selectedConversationId,
  );
  const [messageInput, setMessageInput] = useState('');
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop =
        messagesContainerRef.current.scrollHeight;
    }
  }, [selectedConversation?.messages]);

  const handleSendMessage = () => {
    if (messageInput.trim() && selectedConversation) {
      const currentTime = getGameTime();
      addMessage(selectedConversation.id, {
        id: String(selectedConversation.messages.length + 1),
        sender: 'You',
        senderAvatar: '/avatars/y.jpg',
        content: messageInput,
        timestamp: currentTime,
        isOwn: true,
      });
      // Automatic reply
      addMessage(selectedConversation.id, {
        id: String(selectedConversation.messages.length + 2),
        sender: selectedConversation.name,
        senderAvatar: selectedConversation.avatar,
        content:
          "Automatic reply: Thank you for your message. I'm currently unavailable and will respond as soon as possible.",
        timestamp: currentTime,
        isOwn: false,
      });
      playSound('chat-send');
      setMessageInput('');
    }
  };

  return (
    <div className="h-full flex bg-white">
      {/* Sidebar */}
      <div className="w-42 border-r border-gray-200 flex flex-col overflow-hidden bg-gray-50">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-semibold text-gray-900">Chat</h1>
            <Dot size={20} strokeWidth={1.5} className="text-gray-600" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Conversations */}
          <div className="space-y-1">
            {conversations.map((conv) => (
              <button
                key={conv.id}
                onClick={() => {
                  playSound('select');
                  selectConversation(conv.id);
                }}
                className={`w-full text-left px-3 py-2 text-sm rounded transition-colors hover:bg-gray-200 ${
                  selectedConversationId === conv.id ? 'bg-gray-200' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-gray-700 font-medium">{conv.name}</p>
                      <div className="text-xs text-gray-600 truncate flex-shrink-0">
                        {conv.messages[conv.messages.length - 1].timestamp}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-white border border-gray-900 flex-shrink-0" />
                      <p className="text-xs text-gray-500">offline</p>
                    </div>
                  </div>
                  {conv.unreadCount > 0 && (
                    <span className="text-xs font-semibold text-gray-600 bg-white px-2 py-0.5 rounded-full flex-shrink-0">
                      {conv.unreadCount}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Warning Banners */}
        <div className="px-6 py-3 space-y-2 bg-gray-50 border-b border-gray-200 flex-shrink-0">
          {!ndaSigned && (
            <div className="flex items-start gap-3 p-3 bg-white border border-gray-300 rounded text-sm">
              <AlertCircle
                size={16}
                strokeWidth={1.5}
                className="text-gray-700 flex-shrink-0 mt-0.5"
              />
              <div className="text-gray-700">
                <p className="font-medium">NDA Required</p>
                <p className="text-xs text-gray-600 mt-1">
                  You must sign the NDA to access chat starting January 29,
                  2030.
                </p>
              </div>
            </div>
          )}
          <div className="flex items-start gap-3 p-3 bg-white border border-gray-300 rounded text-sm">
            <AlertCircle
              size={16}
              strokeWidth={1.5}
              className="text-gray-700 flex-shrink-0 mt-0.5"
            />
            <div className="text-gray-700">
              <p className="font-medium">Messages Deleted</p>
              <p className="text-xs text-gray-600 mt-1">
                All messages prior to January 29, 2030 have been deleted due to
                the security incident.
              </p>
            </div>
          </div>
        </div>

        {/* Messages */}
        {selectedConversation && (
          <div
            ref={messagesContainerRef}
            className={`flex-1 overflow-y-auto p-6 space-y-4 ${
              !ndaSigned ? 'blur-sm' : ''
            }`}
          >
            {selectedConversation.messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-3 ${message.isOwn ? 'flex-row-reverse' : ''}`}
              >
                <Avatar sender={message.sender} />
                <div
                  className={`flex-1 ${
                    message.isOwn ? 'items-end' : 'items-start'
                  } flex flex-col`}
                >
                  {!message.isOwn && (
                    <div className="flex items-baseline gap-2">
                      <p className="font-semibold text-sm text-gray-900">
                        {message.sender}
                      </p>
                      <p className="text-xs text-gray-500">
                        {message.timestamp}
                      </p>
                    </div>
                  )}
                  <p
                    className={`text-sm  px-3 py-2 rounded ${
                      message.isOwn
                        ? 'bg-gray-900 text-white rounded-bl-none'
                        : 'bg-gray-100 text-gray-700 rounded-bl-none'
                    }`}
                  >
                    {message.id === '5' ? (
                      <span>
                        Those we haven&apos;t figured out how to force set yet,
                        just leave them{' '}
                        <span className="text-red-500">as is.</span>
                      </span>
                    ) : (
                      message.content
                    )}
                  </p>
                  {message.isOwn && (
                    <p className="text-xs text-gray-500 mt-1">
                      {message.timestamp}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Message input */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex items-end gap-2">
            <button className="p-2 hover:bg-gray-100 rounded transition-colors flex-shrink-0">
              <Paperclip
                size={18}
                strokeWidth={1.5}
                className="text-gray-600"
              />
            </button>
            <input
              type="text"
              placeholder="Type your message..."
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              className="flex-1 px-4 py-2 border border-gray-300 rounded text-sm outline-none hover:border-gray-400 focus:border-gray-500 transition-colors"
            />
            <button
              onClick={handleSendMessage}
              className="p-2 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
            >
              <Send size={18} strokeWidth={1.5} className="text-gray-600" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
